<template>
    <div class="merchant-pay">
        <!-- 页面标题 -->
        <div class="page-header">
            <div class="header-title">商家支付</div>
        </div>

        <!-- 手机号验证步骤 -->
        <div v-if="currentStep === 'phone'" class="step-container">
            <div class="step-title">请输入手机号</div>
            <div class="form-container">
                <div class="phone-input-wrapper">
                    <van-icon name="phone-o" class="input-icon" />
                    <input
                        v-model="phone"
                        type="tel"
                        placeholder="请输入手机号"
                        maxlength="11"
                        class="phone-input"
                        @blur="validatePhone"
                        @input="handlePhoneInput"
                    />
                </div>
                <div class="form-actions">
                    <van-button 
                        type="primary" 
                        size="large" 
                        :loading="phoneLoading"
                        :disabled="!isValidPhone"
                        @click="checkPhone"
                    >
                        下一步
                    </van-button>
                </div>
            </div>
        </div>

        <!-- 注册步骤 -->
        <div v-if="currentStep === 'register'" class="step-container">
            <div class="step-title">用户注册</div>
            <div class="form-container">
                <van-field
                    v-model="phone"
                    type="tel"
                    label="手机号"
                    readonly
                />
                <van-field
                    v-model="authCode"
                    type="number"
                    label="验证码"
                    placeholder="请输入验证码"
                    maxlength="6"
                >
                    <template #button>
                        <van-button 
                            size="small" 
                            type="primary" 
                            :disabled="codeCountdown > 0"
                            @click="sendAuthCode"
                        >
                            {{ codeCountdown > 0 ? `${codeCountdown}s` : '获取验证码' }}
                        </van-button>
                    </template>
                </van-field>
                <div class="form-actions">
                    <van-button 
                        type="primary" 
                        size="large" 
                        :loading="registerLoading"
                        :disabled="!authCode"
                        @click="registerUser"
                    >
                        注册并继续
                    </van-button>
                </div>
            </div>
        </div>

        <!-- 支付信息步骤 -->
        <div v-if="currentStep === 'payment'" class="step-container">
            <div class="step-title">支付信息</div>
            
            <!-- 商户信息 -->
            <div class="merchant-info">
                <div class="info-title">商户信息</div>
                <div v-if="merchantInfo" class="merchant-details">
                    <div class="info-item">
                        <span class="label">商户ID：</span>
                        <span class="value">{{ merchantInfo.id || merchantId }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label">商铺名称：</span>
                        <span class="value">{{ merchantInfo.storeName || '未设置' }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label">商家姓名：</span>
                        <span class="value">{{ hideName(merchantInfo.fullName) || '未设置' }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label">联系电话：</span>
                        <span class="value">{{ hidePhone(merchantInfo.phone) || phone }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label">营业状态：</span>
                        <span class="value" :class="getBusinessStatus(merchantInfo).statusClass">
                            {{ getBusinessStatus(merchantInfo).statusText }}
                        </span>
                    </div>
                    <div class="info-item" v-if="getBusinessStatus(merchantInfo).businessHours">
                        <span class="label">营业时间：</span>
                        <span class="value">{{ getBusinessStatus(merchantInfo).businessHours }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label">商家状态：</span>
                        <span class="value" :class="getMerchantStatusClass(merchantInfo.merchantStatus)">
                            {{ getMerchantStatusText(merchantInfo.merchantStatus) }}
                        </span>
                    </div>
                    <div class="info-item" v-if="merchantInfo.province || merchantInfo.city">
                        <span class="label">所在地区：</span>
                        <span class="value">{{ getLocationText(merchantInfo) }}</span>
                    </div>
                </div>
                <div v-else class="merchant-loading">
                    <van-loading size="16" />
                    <span>正在加载商户信息...</span>
                </div>
            </div>

            <!-- 支付金额 -->
            <div class="payment-amount">
                <div class="amount-title">支付金额</div>
                <van-field
                    v-model="paymentAmount"
                    type="digit"
                    placeholder="请输入支付金额"
                    label="金额"
                >
                    <template #right-icon>
                        <span class="currency">元</span>
                    </template>
                </van-field>
            </div>

            <!-- 支付渠道 -->
            <PaymentChannel
                v-model="selectedChannel"
                :brand-id="brandId"
                @channel-change="onChannelChange"
            />

            <!-- 支付按钮 -->
            <div class="form-actions">
                <van-button 
                    type="primary" 
                    size="large" 
                    :loading="payLoading"
                    :disabled="!canPay"
                    @click="submitPayment"
                >
                    确认支付
                </van-button>
            </div>
        </div>

        <!-- 加载状态 -->
        <van-loading v-if="globalLoading" class="global-loading" />
    </div>
</template>

<script>
import { ref, computed, getCurrentInstance } from 'vue';
import { Field , Icon , Button , Checkbox , NavBar , Area , Popup , Loading } from 'vant';
import storage from 'store';
import { ACCESS_TOKEN, BRANDID } from '@/utils/types';
import PaymentChannel from '@/components/PaymentChannel.vue';

export default {
    name: 'MerchantPay',
    components: {
        PaymentChannel,
        [Field.name]:Field,
        [Icon.name]:Icon,
        [Button.name]:Button,
        [Checkbox.name]:Checkbox,
        [NavBar.name]:NavBar,
        [Area.name]:Area,
        [Popup.name]:Popup,
        [Loading.name]:Loading,
    },
    setup() {
        const { proxy } = getCurrentInstance();
        const brandId = proxy.getHashParam('brandId');
        const parentPhone = proxy.getHashParam('phone');
        const merchantId = proxy.getHashParam('merchantId');
        
        // 响应式数据
        const currentStep = ref('phone'); // phone, register, payment
        const phone = ref('');
        const authCode = ref('');
        const paymentAmount = ref('');
        const selectedChannel = ref(null);
        const merchantInfo = ref(null);
        
        // 加载状态
        const phoneLoading = ref(false);
        const registerLoading = ref(false);
        const payLoading = ref(false);
        const globalLoading = ref(false);
        const codeCountdown = ref(0);
        
        // 计算属性
        const isValidPhone = computed(() => {
            return /^1[3-9]\d{9}$/.test(phone.value);
        });
        
        const canPay = computed(() => {
            return paymentAmount.value && 
                   parseFloat(paymentAmount.value) > 0 && 
                   selectedChannel.value;
        });

        // 获取系统的token
        const getToken = async () => {
            try {
                storage.set(ACCESS_TOKEN, '', '');
                const res = await proxy.$http.brandApi.getToken(brandId);
                if (res.success) {
                    storage.set(ACCESS_TOKEN, res[ACCESS_TOKEN], res.expires_in);
                }
            } catch (error) {
                console.error('获取token失败:', error);
            }
        };

        // 查询商户信息
        const getMerchantInfo = async () => {
            try {
                const res = await proxy.$http.merchantApi.queryGoodsMerchantInfo({
                    id: merchantId,
                    brandId: brandId,
                    page: 0,
                    size: 1,
                    isPage: 1
                });
                
                if (res.success && res.data && res.data.content && res.data.content.length > 0) {
                    merchantInfo.value = res.data.content[0];
                    console.log('商户信息:', merchantInfo.value);
                } else {
                    console.warn('未找到商户信息');
                }
            } catch (error) {
                console.error('查询商户信息失败:', error);
            }
        };

        // 验证手机号格式
        const validatePhone = () => {
            if (phone.value && !isValidPhone.value) {
                proxy.$toast('请输入正确的手机号');
            }
        };

        // 手机号输入处理
        const handlePhoneInput = (value) => {
            // phone.value = value;
            console.log('手机号输入:', value);
        };

        // 检查手机号是否已注册
        const checkPhone = async () => {
            if (!isValidPhone.value) {
                proxy.$toast('请输入正确的手机号');
                return;
            }

            try {
                phoneLoading.value = true;
                const res = await proxy.$http.loginApi.userIsExist(phone.value);
                
                if (res.success) {
                    // if (res.data === true) {
                    //     // 用户已存在，直接进入支付步骤
                    //     proxy.$toast('用户已注册，进入支付流程');
                    // } else {
                    //     // 用户不存在，自动注册
                    //     proxy.$toast('用户未注册，正在自动注册...');
                    // }
                    currentStep.value = 'payment';
                    // 进入支付步骤时查询商户信息
                    await getMerchantInfo();
                } else {
                    // proxy.$toast(res.message || '检查手机号失败');
                    await autoRegister();
                }
            } catch (error) {
                console.error('检查手机号失败:', error);
                proxy.$toast('检查手机号失败');
            } finally {
                phoneLoading.value = false;
            }
        };

        // 自动注册用户
        const autoRegister = async () => {
            try {
                // 显示加载状态
                globalLoading.value = true;
                
                // 先发送验证码
                const codeRes = await proxy.$http.loginApi.getCode(phone.value);
                if (!codeRes.success) {
                    proxy.$toast(codeRes.message || '发送验证码失败');
                    globalLoading.value = false;
                    return;
                }

                // 等待验证码发送完成
                await new Promise(resolve => setTimeout(resolve, 2000));

                // 尝试使用通用验证码进行注册
                // 如果系统有特殊的自动注册验证码，请替换 '888888'
                const registerRes = await proxy.$http.loginApi.register(
                    phone.value,
                    '888888', // 自动注册验证码，可能需要根据实际情况调整
                    'hdd123456', // 默认密码
                    parentPhone
                );
                
                if (registerRes.success) {
                    proxy.$toast('自动注册成功，进入支付流程');
                    currentStep.value = 'payment';
                } else {
                    // 如果自动注册失败，进入手动注册流程
                    proxy.$toast('自动注册失败，请手动输入验证码完成注册');
                    currentStep.value = 'register';
                    // 自动发送验证码
                    startCountdown();
                }
            } catch (error) {
                console.error('自动注册失败:', error);
                proxy.$toast('自动注册失败，请手动完成注册');
                currentStep.value = 'register';
                // 自动发送验证码
                startCountdown();
            } finally {
                globalLoading.value = false;
            }
        };

        // 发送验证码
        const sendAuthCode = async () => {
            if (!isValidPhone.value) {
                proxy.$toast('请输入正确的手机号');
                return;
            }

            try {
                const res = await proxy.$http.loginApi.getCode(phone.value);
                if (res.success) {
                    proxy.$toast('验证码已发送');
                    startCountdown();
                } else {
                    proxy.$toast(res.message || '发送验证码失败');
                }
            } catch (error) {
                console.error('发送验证码失败:', error);
                proxy.$toast('发送验证码失败');
            }
        };

        // 开始倒计时
        const startCountdown = () => {
            codeCountdown.value = 60;
            const timer = setInterval(() => {
                codeCountdown.value--;
                if (codeCountdown.value <= 0) {
                    clearInterval(timer);
                }
            }, 1000);
        };

        // 注册用户
        const registerUser = async () => {
            if (!authCode.value) {
                proxy.$toast('请输入验证码');
                return;
            }

            try {
                registerLoading.value = true;
                const res = await proxy.$http.loginApi.register(
                    phone.value,
                    authCode.value,
                    'hdd123456', // 默认密码
                    parentPhone
                );
                
                if (res.success) {
                    proxy.$toast('注册成功');
                    currentStep.value = 'payment';
                } else {
                    proxy.$toast(res.message || '注册失败');
                }
            } catch (error) {
                console.error('注册失败:', error);
                proxy.$toast('注册失败');
            } finally {
                registerLoading.value = false;
            }
        };

        // 支付渠道变化
        const onChannelChange = (channel) => {
            console.log('选择支付渠道:', channel);
        };

        // 获取支付类型
        const getPayType = (channelId) => {
            const payTypeMap = {
                1: 1,   // 余额
                11: 11, // 51微信
                13: 13, // 新支付宝
                14: 14  // 51支付宝
            };
            return payTypeMap[channelId] || 1;
        };

        // 获取商户状态文本
        const getMerchantStatusText = (status) => {
            const statusMap = {
                0: '审核中',
                1: '开启中',
                2: '关闭中'
            };
            return statusMap[status] || '未知状态';
        };

        // 获取商户状态样式类
        const getMerchantStatusClass = (status) => {
            const classMap = {
                0: 'status-pending',
                1: 'status-approved',
                2: 'status-rejected'
            };
            return classMap[status] || 'status-unknown';
        };

        // 获取地区文本
        const getLocationText = (merchant) => {
            const parts = [];
            if (merchant.province) parts.push(merchant.province);
            if (merchant.city) parts.push(merchant.city);
            if (merchant.area) parts.push(merchant.area);
            if (merchant.town) parts.push(merchant.town);
            return parts.join(' ') || '未设置';
        };

        // 隐藏姓名（显示第一个字，其余用*代替）
        const hideName = (name) => {
            if (!name) return '';
            if (name.length <= 1) return name;
            return name.charAt(0) + '*'.repeat(name.length - 1);
        };

        // 隐藏手机号（显示前3位和后4位，中间用*代替）
        const hidePhone = (phone) => {
            if (!phone) return '';
            if (phone.length < 7) return phone;
            return phone.substring(0, 3) + '****' + phone.substring(phone.length - 4);
        };

        // 根据营业时间判断营业状态
        const getBusinessStatus = (merchant) => {
            if (!merchant) return { isOpen: false, statusText: '已打烊', statusClass: 'status-closed' };
            
            // 如果商家手动设置了营业状态，优先使用手动设置
            // if (merchant.tradeStatus !== undefined && merchant.tradeStatus !== null) {
            //     const isOpen = merchant.tradeStatus === 1;
            //     return {
            //         isOpen,
            //         statusText: isOpen ? '正常营业' : '已打烊',
            //         statusClass: isOpen ? 'status-open' : 'status-closed'
            //     };
            // }

            // 如果没有营业时间设置，默认显示为营业中
            if (!merchant.tradeStartTime || !merchant.tradeEndTime) {
                return {
                    isOpen: true,
                    statusText: '正常营业',
                    statusClass: 'status-open'
                };
            }

            try {
                const now = new Date();
                const currentTime = now.getHours() * 60 + now.getMinutes(); // 当前时间转换为分钟

                // 解析营业开始时间
                const startTimeParts = merchant.tradeStartTime.split(':');
                const startTime = parseInt(startTimeParts[0]) * 60 + parseInt(startTimeParts[1]);

                // 解析营业结束时间
                const endTimeParts = merchant.tradeEndTime.split(':');
                let endTime = parseInt(endTimeParts[0]) * 60 + parseInt(endTimeParts[1]);

                // 处理跨天营业的情况（如22:00-02:00）
                if (endTime < startTime) {
                    endTime += 24 * 60; // 结束时间加24小时
                    const currentTimeWithDay = currentTime + (now.getDay() * 24 * 60);
                    const isOpen = currentTimeWithDay >= startTime && currentTimeWithDay < endTime;
                    
                    return {
                        isOpen,
                        statusText: isOpen ? '正常营业' : '已打烊',
                        statusClass: isOpen ? 'status-open' : 'status-closed',
                        businessHours: `${merchant.tradeStartTime}-${merchant.tradeEndTime}`
                    };
                } else {
                    // 正常营业时间（如09:00-18:00）
                    const isOpen = currentTime >= startTime && currentTime < endTime;
                    console.log('当前时间:', currentTime);
                    console.log('开始时间:', startTime);
                    console.log('结束时间:', endTime);
                    console.log('是否营业:', isOpen);
                    return {
                        isOpen,
                        statusText: isOpen ? '正常营业' : '已打烊',
                        statusClass: isOpen ? 'status-open' : 'status-closed',
                        businessHours: `${merchant.tradeStartTime}-${merchant.tradeEndTime}`
                    };
                }
            } catch (error) {
                console.error('解析营业时间失败:', error);
                return {
                    isOpen: true,
                    statusText: '正常营业',
                    statusClass: 'status-open'
                };
            }
        };

        // 提交支付
        const submitPayment = async () => {
            if (!canPay.value) {
                proxy.$toast('请完善支付信息');
                return;
            }

            try {
                payLoading.value = true;
                
                // 提交商家收款订单
                const orderRes = await proxy.$http.merchantApi.submitGoodsMerchantPaymentOrder({
                    merchantId: merchantId,
                    paymentAmount: paymentAmount.value,
                    remarks: '商家支付',
                    voucherImage: '',
                    cardId: ''
                });

                if (orderRes.success) {
                    // 支付订单
                    const payRes = await proxy.$http.merchantApi.payGoodsMerchantPaymentOrder({
                        orderCode: orderRes.data.orderCode,
                        payType: getPayType(selectedChannel.value.id), // 根据渠道ID获取支付类型
                        temptime: Date.now(),
                        accountType: 1
                    });

                    if (payRes.success) {
                        proxy.$toast('支付成功');
                        // 这里可以跳转到支付成功页面或返回上一页
                    } else {
                        proxy.$toast(payRes.message || '支付失败');
                    }
                } else {
                    proxy.$toast(orderRes.message || '请登录“艺选臻品”App 扫码下单');
                }
            } catch (error) {
                console.error('支付失败:', error);
                proxy.$toast('支付失败');
            } finally {
                payLoading.value = false;
            }
        };

        // 初始化
        getToken();

        return {
            currentStep,
            phone,
            authCode,
            paymentAmount,
            selectedChannel,
            merchantInfo,
            phoneLoading,
            registerLoading,
            payLoading,
            globalLoading,
            codeCountdown,
            isValidPhone,
            canPay,
            brandId,
            parentPhone,
            merchantId,
            validatePhone,
            handlePhoneInput,
            checkPhone,
            autoRegister,
            sendAuthCode,
            registerUser,
            onChannelChange,
            getPayType,
            getMerchantStatusText,
            getMerchantStatusClass,
            getLocationText,
            hideName,
            hidePhone,
            getBusinessStatus,
            submitPayment
        };
    }
}
</script>

<style scoped>
.merchant-pay {
    min-height: 100vh;
    background-color: #f5f5f5;
}

.page-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 20px 16px;
    color: white;
}

.header-title {
    font-size: 18px;
    font-weight: 600;
    text-align: center;
}

.step-container {
    padding: 20px 16px;
}

.step-title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 20px;
    text-align: center;
}

.form-container {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-container .van-field {
    pointer-events: auto;
}

.form-container .van-field__control {
    pointer-events: auto;
    user-select: text;
}

.phone-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    background: #f7f8fa;
    border-radius: 6px;
    padding: 0 12px;
    margin-bottom: 16px;
}

.input-icon {
    color: #969799;
    margin-right: 8px;
    font-size: 16px;
}

.phone-input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 14px;
    color: #323233;
    padding: 12px 0;
    line-height: 1.5;
}

.phone-input::placeholder {
    color: #c8c9cc;
}

.form-actions {
    margin-top: 20px;
}

.merchant-info {
    background: white;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.info-title {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 12px;
}

.info-item {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
}

.info-item:last-child {
    margin-bottom: 0;
}

.label {
    color: #666;
    font-size: 14px;
}

.value {
    color: #333;
    font-size: 14px;
    font-weight: 500;
}

.merchant-details {
    margin-top: 8px;
}

.merchant-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    color: #666;
    font-size: 14px;
}

.merchant-loading span {
    margin-left: 8px;
}

.status-open {
    color: #07c160;
    font-weight: 600;
}

.status-closed {
    color: #ff6b6b;
    font-weight: 600;
}

.status-pending {
    color: #ff9500;
    font-weight: 600;
}

.status-approved {
    color: #07c160;
    font-weight: 600;
}

.status-rejected {
    color: #ff6b6b;
    font-weight: 600;
}

.status-unknown {
    color: #999;
    font-weight: 600;
}

.payment-amount {
    background: white;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.amount-title {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 12px;
}

.currency {
    color: #666;
    font-size: 14px;
}

.global-loading {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 9999;
}
</style>